/**
 * LockRoute — PIN-protected entry gate.
 *
 * Single-input flow:
 *
 *   - The End_User types her passphrase. The same input box is used for
 *     first-time setup AND for everyday unlock — there is no separate
 *     "create" screen, no "confirm password" field, no per-device
 *     onboarding step.
 *
 *   - On submit:
 *       * If a credential is already stored on this device, the typed
 *         password is verified against it. Wrong → polite error, clear
 *         the field, refocus.
 *       * If NO credential is stored yet (this is either the very first
 *         time on this device or the user is signing in from a fresh
 *         phone / new browser), the typed password is saved as the
 *         credential for this device. Because the lock module uses a
 *         deterministic shared salt, the same password produces the
 *         exact same hash on every device — typing the same password
 *         on a phone that has never seen this app before is enough to
 *         unlock the same "account" as the desktop. No sync server, no
 *         account creation step, no confusion.
 *
 * Why a single input instead of a "create + confirm" pair:
 *
 *   The previous flow showed two boxes and called the screen "yeni
 *   şifre belirle" on every brand-new device, which made the user
 *   think she was creating a separate phone-only password. With a
 *   single input the screen reads the same on every device — "şifren"
 *   — and the cross-device behaviour becomes obvious: type the
 *   password you remember, you're in.
 *
 * The PIN never appears outside the input element (which is rendered
 * `type="password"` so the platform masks the characters). The PIN is
 * never persisted directly; only a salted PBKDF2 hash lives in
 * localStorage, so even with full disk access an attacker would have
 * to brute-force the passphrase.
 */

import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';

import {
  isLockConfigured,
  markDeviceTrusted,
  setLockPin,
  verifyLockPin,
} from '../infra/lock';

import styles from './LockRoute.module.css';

interface LockRouteProps {
  /** Invoked after a successful unlock or first-time setup. */
  onUnlocked: () => void;
}

export function LockRoute({ onUnlocked }: LockRouteProps): ReactElement {
  // We capture whether a credential exists on mount so the wording stays
  // stable even after `setLockPin` resolves (which would flip
  // `isLockConfigured()` mid-render).
  const [hasExisting] = useState<boolean>(() => isLockConfigured());
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus on mount so typing starts immediately on every device.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    void (async (): Promise<void> => {
      const trimmed = pin.trim();
      if (trimmed.length < 4) {
        setError('en az 4 karakter olsun.');
        return;
      }
      setBusy(true);
      try {
        if (isLockConfigured()) {
          // Existing credential on this device — verify.
          const ok = await verifyLockPin(trimmed);
          if (!ok) {
            setError('şifre yanlış, bir daha dene.');
            setPin('');
            inputRef.current?.focus();
            return;
          }
          markDeviceTrusted();
          onUnlocked();
          return;
        }

        // No credential on this device yet. Save what she typed as the
        // credential for this device. Thanks to the deterministic shared
        // salt in lock.ts, this hash matches whatever she set on her
        // other devices when she typed the same password there — so the
        // app behaves like a single shared account without any server.
        await setLockPin(trimmed);
        markDeviceTrusted();
        onUnlocked();
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <main
      className={styles.lock}
      aria-labelledby="lock-heading"
      data-testid="lock-route"
    >
      <div className={styles.card}>
        <div className={styles.heart} aria-hidden="true">💗</div>
        <h1 id="lock-heading" className={styles.title}>
          {hasExisting ? 'hoş geldin canım' : 'Bir şifre belirle'}
        </h1>
        <p className={styles.intro}>
          {hasExisting
            ? 'şifreni gir bakayım.'
            : 'bu uygulamayı sadece sen açabilesin diye şifreni belirle miss world'}
        </p>
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          autoComplete="off"
        >
          <input
            ref={inputRef}
            type="password"
            inputMode="text"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="şifren"
            aria-label="şifren"
            className={styles.input}
            autoComplete="new-password"
            data-testid="lock-pin-input"
          />
          <button
            type="submit"
            className={styles.btn}
            disabled={busy}
            data-testid="lock-submit"
          >
            {busy ? '...' : hasExisting ? 'aç' : 'devam'}
          </button>
          {error !== null ? (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          ) : null}
        </form>
      </div>
    </main>
  );
}
